import { formatSubmittedAnswer } from '@mathflat/domain/Scoring'
import type {
  GroupWorksheetScoring,
  StudentWorksheetVirtualScoring,
} from '@mathflat/domain/StudentWorksheet'
import { computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID, EMPTY_STRING_ID } from 'src/constant/common'

import { typeSafeAssign } from '@/utils/common'

import { DTO } from '../DTO'
import { ProblemScoring } from './ProblemScoring'

export class GroupStudentWorksheetProblem extends DTO {
  studentId = EMPTY_STRING_ID
  studentName = ''
  studentWorksheetId = EMPTY_NUMBER_ID
  scoringList: ProblemScoring[] = [
    new ProblemScoring({
      worksheetProblemId: 0,
      result: 'CORRECT' as 'CORRECT' | 'WRONG' | 'NONE',
      virtualResult: 'NONE',
      virtualUserAnswer: '',
    }),
  ]

  constructor(
    data?: Pick<GroupWorksheetScoring, 'studentId' | 'studentName' | 'studentWorksheetId'> & {
      scoringList: Pick<
        GroupWorksheetScoring['scoringList'][number],
        'worksheetProblemId' | 'result'
      >[]
      virtualScores?: StudentWorksheetVirtualScoring['virtualScores']
    },
  ) {
    super()
    makeObservable(this, {
      studentId: observable,
      studentName: observable,
      studentWorksheetId: observable,
      scoringList: observable,
      score: computed,
      status: computed,
    })

    if (data)
      typeSafeAssign(this, {
        studentId: data.studentId,
        studentName: data.studentName,
        studentWorksheetId: data.studentWorksheetId,
        scoringList: data.scoringList.map((scoring) => {
          const virtualScoring = data.virtualScores?.find(
            (virtualScoring) => virtualScoring.worksheetProblemId === scoring.worksheetProblemId,
          )

          return new ProblemScoring({
            ...scoring,
            virtualResult: virtualScoring?.result ?? 'NONE',
            virtualUserAnswer: (virtualScoring && formatSubmittedAnswer(virtualScoring)) ?? '',
          })
        }),
      })
  }

  get score() {
    const allProblemNumber = this.scoringList.length
    const correctAnswerNumber = this.scoringList.filter(
      (scoring) => scoring.result === 'CORRECT',
    ).length

    return Math.floor(correctAnswerNumber * (100 / allProblemNumber))
  }

  get status() {
    if (this.scoringList.every((scoring) => scoring.result !== 'NONE')) {
      return 'COMPLETE'
    }

    if (this.scoringList.some((scoring) => scoring.result !== 'NONE')) {
      return 'PROGRESS'
    }

    return 'INCOMPLETE'
  }
}
